import { useState } from 'react';
import { Divider, Drawer, FormControlLabel, IconButton, Link as MuiLink, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { ArrowSquareOutIcon, CopyIcon, XIcon } from '@phosphor-icons/react';

import { useDebouncedBlockContent } from '@/hooks/use-debounced-block-content';
import { useSite } from '@/hooks/use-site';
import { usePublicSiteUrl } from '@/hooks/use-public-site-url';
import type { Site } from '@/types/database';

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  site: Site;
}

// Same env-driven Hub URL already used for auth redirects (see
// src/layouts/app/index.tsx) — not a guessed path, the actual base URL
// this app already relies on elsewhere.
const HUB_URL = (import.meta.env.VITE_HUB_URL ?? "https://scoutingops.com") as string;

type SeoDraft = Pick<Site, 'seo_title' | 'seo_description'>;

// The "Settings" destination (spec section 5). Scoped to what's actually
// site-builder's own concern: SEO defaults and the public URL. Domain
// management already lives in Scouting Ops Hub (see
// docs/site-builder-overhaul.md's Phase 3 history) and isn't duplicated
// here — this just links out to it. Role-based email aliases, analytics,
// and integrations are Phase F/G scope, not built yet.
export function SettingsDrawer({ open, onClose, site }: SettingsDrawerProps) {
  const { save } = useSite();
  const { url: publicUrl } = usePublicSiteUrl();
  const [copied, setCopied] = useState(false);
  const [draft, update] = useDebouncedBlockContent<SeoDraft>(
    { seo_title: site.seo_title, seo_description: site.seo_description },
    (next) => void save(next)
  );

  const handleCopy = () => {
    if (!publicUrl) return;
    void navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 } } } }}>
      <Stack direction="row" sx={{ alignItems: 'center', px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
          Settings
        </Typography>
        <IconButton onClick={onClose} aria-label="Close">
          <XIcon size={20} />
        </IconButton>
      </Stack>

      <Stack spacing={2.5} sx={{ p: 2.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
          Public URL
        </Typography>
        {publicUrl ? (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <MuiLink href={publicUrl} target="_blank" rel="noopener noreferrer" sx={{ flex: 1, wordBreak: 'break-all' }}>
              {publicUrl.replace(/^https?:\/\//, '')}
            </MuiLink>
            <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
              <IconButton size="small" onClick={handleCopy} aria-label="Copy site link">
                <CopyIcon size={16} />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not published yet.
          </Typography>
        )}
        <MuiLink
          href={HUB_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 14 }}
        >
          Connect a custom domain in Scouting Ops Hub
          <ArrowSquareOutIcon size={14} />
        </MuiLink>

        <Divider />

        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
          Search &amp; sharing
        </Typography>
        <TextField
          label="SEO title"
          value={draft.seo_title ?? ''}
          onChange={(event) => update({ ...draft, seo_title: event.target.value || null })}
          helperText="Shown in search results and link previews. Falls back to your unit name."
          fullWidth
        />
        <TextField
          label="SEO description"
          value={draft.seo_description ?? ''}
          onChange={(event) => update({ ...draft, seo_description: event.target.value || null })}
          multiline
          minRows={3}
          fullWidth
        />

        <Divider />

        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
          Accessibility
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={site.accessibility_launcher_enabled}
              onChange={(event) => void save({ accessibility_launcher_enabled: event.target.checked })}
            />
          }
          label="Show accessibility preferences launcher on published site"
        />
        <Typography variant="body2" color="text.secondary">
          Lets visitors adjust text size, contrast, motion, and other display preferences on their own device. This does not change your site&apos;s saved design.
        </Typography>
      </Stack>
    </Drawer>
  );
}
